// /mnt/mantenimientos/frontend/src/layout/TecnicoAppShell.jsx

import React, { useMemo, useState } from "react";
import { useCliente } from "../context/ClienteContext";
import { isTecnico, getRol } from "../utils/auth";

import Topbar from "./Topbar";
import Sidebar from "./Sidebar";

import { MODULES_REGISTRY } from "../modules/registry";
import { getModuleById } from "../modules/helpers";
import { MODULE_IDS } from "../modules/moduleIds";

export default function TecnicoAppShell() {
  const {
    clienteInfo,
    clienteSeleccionadoId,
    ordenadores,
    activeModule,
    setActiveModule,
    clientes,
    effectiveAccess,
  } = useCliente();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const hasCliente = !!clienteInfo;
  const isTech = isTecnico();
  const rol = getRol();

  const availableModules = useMemo(() => {
    if (!effectiveAccess.loaded) return [];
    const allowed = new Set(effectiveAccess.moduleKeys || []);
    // Los permisos asignados desde Usuarios y roles son autoritativos.
    // No duplicamos aquí restricciones por rol que ocultarían módulos concedidos.
    return MODULES_REGISTRY.filter((module) => allowed.has(module.id));
  }, [rol, effectiveAccess.loaded, effectiveAccess.moduleKeys]);

  const activeModuleDef = useMemo(() => {
    return getModuleById(availableModules, activeModule) || null;
  }, [availableModules, activeModule]);

  function SelectClienteCard() {
    return (
      <div className="card">
        <div style={{ fontWeight: 900 }}>Selecciona un cliente</div>
        <div style={{ color: "var(--muted)", marginTop: 6 }}>
          Usa el selector superior para comenzar.
        </div>
      </div>
    );
  }

  function ModuleInaccessibleCard() {
    return (
      <div className="card">
        <div style={{ fontWeight: 900 }}>Módulo no disponible</div>
        <div style={{ color: "var(--muted)", marginTop: 6 }}>
          El módulo actual no está disponible para este rol o configuración.
        </div>
      </div>
    );
  }

  function renderModule() {
    if (!effectiveAccess.loaded) return <div className="card">Cargando accesos…</div>;
    if (!activeModuleDef) {
      return <ModuleInaccessibleCard />;
    }

    if (activeModuleDef.requiresCliente && !hasCliente && !activeModuleDef.supportsGlobalContext) {
      return <SelectClienteCard />;
    }

    const Component = activeModuleDef.component;
    const extraProps =
      typeof activeModuleDef.componentProps === "function"
        ? activeModuleDef.componentProps({ clientes, clienteInfo, clienteSeleccionadoId, ordenadores })
        : {};

    if (!Component) {
      return (
        <div className="card">
          <div style={{ fontWeight: 900 }}>Módulo en construcción</div>
          <div style={{ color: "var(--muted)", marginTop: 6 }}>
            Este módulo estará disponible próximamente.
          </div>
        </div>
      );
    }

    return <Component {...extraProps} />;
  }

  // Seguridad extra: si el módulo activo deja de existir o deja de estar permitido,
  // lo llevamos a uno válido.
  React.useEffect(() => {
    if (!availableModules.length) return;

    const stillExists = availableModules.some((m) => m.id === activeModule);
    if (!stillExists) {
      const fallback =
        availableModules.find((m) => m.id === MODULE_IDS.DASHBOARD_SUPERVISOR) ||
        availableModules.find((m) => m.id === MODULE_IDS.DASHBOARD) ||
        availableModules[0];

      if (fallback) setActiveModule(fallback.id);
    }
  }, [availableModules, activeModule, setActiveModule]);

  return (
    <div className="saas saas-full">
      <Topbar mobileMenuOpen={mobileMenuOpen} onMobileMenuToggle={() => setMobileMenuOpen((open) => !open)} />

      <div className="content">
        <div className="app-grid">
          <Sidebar mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

          <main className={`main ${activeModuleDef?.workspace ? "main--workspace" : ""}`}>
            {renderModule()}

            {!hasCliente && isTech && (
              <div style={{ marginTop: 10, color: "var(--muted)", fontSize: 12 }}>
                Consejo: selecciona un cliente arriba para habilitar los módulos técnicos.
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
